import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  useId,
  useImperativeHandle,
  type KeyboardEvent,
} from 'react';
import { parseQuickAdd, type Token } from '../lib/parser/index.js';
import { useProjects } from '../stores/projects.js';
import { cn } from '../lib/cn.js';
import { masquerLiens } from '../lib/linkify.js';
import { teinte } from '../lib/teinte.js';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  today: string;
  className?: string;
  autoFocus?: boolean;
  'aria-label'?: string;
}

// Deliberately outside any project color (palette: green, blue, amber, red,
// purple, teal, orange, gray) : la lavande des séries du thème. Un #projet inconnu prend
// l'encre discrète — il n'a pas de couleur à lui.
const DATE_COLOR = 'var(--serie-5)';
const UNKNOWN_PROJECT = 'var(--encre-discrete)';

function normalize(s: string): string {
  return s.toLowerCase().replace(/[-_\s]+/g, '');
}

interface ProjectMention {
  start: number;
  partial: string;
}

function getActiveMention(value: string, caret: number): ProjectMention | null {
  // Comme le parser : un `#` dans un lien est une ancre, pas un projet (même longueur, mêmes indices).
  const text = masquerLiens(value);
  for (let i = caret - 1; i >= 0; i--) {
    const ch = text[i] ?? '';
    if (ch === '#') {
      const partial = text.slice(i + 1, caret);
      if (/^[\p{L}\p{N}_-]*$/u.test(partial)) {
        return { start: i, partial };
      }
      return null;
    }
    if (!/[\p{L}\p{N}_-]/u.test(ch)) return null;
  }
  return null;
}

function slugify(name: string): string {
  return name.trim().replace(/\s+/g, '-');
}

export const HighlightedInput = forwardRef<HTMLInputElement, Props>(
  function HighlightedInput(
    { value, onChange, onKeyDown, placeholder, today, className, autoFocus, 'aria-label': ariaLabel },
    forwardedRef
  ) {
    const listId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const projects = useProjects((s) => s.projects);

    useImperativeHandle(forwardedRef, () => inputRef.current!, []);

    const [caret, setCaret] = useState<number>(0);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [menuDismissed, setMenuDismissed] = useState(false);

    const tokens = useMemo<Token[]>(() => {
      if (!value) return [];
      return parseQuickAdd(value, today).tokens.sort(
        (a, b) => a.start - b.start
      );
    }, [value, today]);

    const segments = useMemo(() => {
      const out: Array<{ text: string; token?: Token }> = [];
      let cursor = 0;
      for (const tok of tokens) {
        if (tok.start > cursor) {
          out.push({ text: value.slice(cursor, tok.start) });
        }
        out.push({ text: value.slice(tok.start, tok.end), token: tok });
        cursor = tok.end;
      }
      if (cursor < value.length) out.push({ text: value.slice(cursor) });
      return out;
    }, [value, tokens]);

    const mention = useMemo(
      () => (menuDismissed ? null : getActiveMention(value, caret)),
      [value, caret, menuDismissed]
    );

    const matches = useMemo(() => {
      if (!mention) return [];
      const q = mention.partial.toLowerCase();
      return projects
        .filter((p) => p.name.toLowerCase().includes(q))
        .slice(0, 8);
    }, [mention, projects]);

    useEffect(() => {
      setSelectedIdx(0);
    }, [matches.length, mention?.partial]);

    useEffect(() => {
      if (!getActiveMention(value, caret)) setMenuDismissed(false);
    }, [value, caret]);

    function syncScroll(): void {
      if (!inputRef.current || !overlayRef.current) return;
      overlayRef.current.scrollLeft = inputRef.current.scrollLeft;
    }

    // iOS Safari doesn't fire `scroll` on inputs while typing — re-sync after
    // every value/caret change so the overlay never drifts from the input.
    useEffect(() => {
      const id = requestAnimationFrame(syncScroll);
      return () => cancelAnimationFrame(id);
    });

    function captureCaret(): void {
      if (!inputRef.current) return;
      setCaret(inputRef.current.selectionStart ?? 0);
    }

    function applyProject(name: string): void {
      if (!mention || !inputRef.current) return;
      const insert = `#${slugify(name)} `;
      const before = value.slice(0, mention.start);
      const after = value.slice(caret);
      const next = before + insert + after;
      const newCaret = (before + insert).length;
      onChange(next);
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = newCaret;
          inputRef.current.selectionEnd = newCaret;
          setCaret(newCaret);
        }
      });
    }

    const menuOpen = mention !== null && matches.length > 0;

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
      if (mention && matches.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIdx((i) => Math.min(i + 1, matches.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIdx((i) => Math.max(i - 1, 0));
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          const p = matches[selectedIdx];
          if (p) {
            e.preventDefault();
            applyProject(p.name);
            return;
          }
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMenuDismissed(true);
          return;
        }
      }
      onKeyDown?.(e);
    }

    // `.saisie-surlignee` du kit : un calque redessine le texte SOUS une saisie transparente.
    // `.menu-etendu` : les projets proposés s'ouvrent sous la saisie, à sa largeur.
    return (
      <div className={cn('menu menu-etendu', className)}>
        <div className="saisie-surlignee">
          <div ref={overlayRef} aria-hidden className="saisie-calque">
            {tokens.length > 0 &&
              segments.map((s, i) => {
                if (!s.token) return <span key={i}>{s.text}</span>;
                if (s.token.type === 'project') {
                  const name = s.token.text.replace(/^#/, '');
                  const proj = projects.find((p) => normalize(p.name) === normalize(name));
                  return (
                    <span key={i} className="surlignage" style={teinte(proj?.color ?? UNKNOWN_PROJECT)}>
                      {s.text}
                    </span>
                  );
                }
                return (
                  <span key={i} className="surlignage" style={teinte(DATE_COLOR)}>
                    {s.text}
                  </span>
                );
              })}
          </div>
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={menuOpen}
            aria-controls={listId}
            aria-autocomplete="list"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onKeyUp={captureCaret}
            onSelect={captureCaret}
            onClick={captureCaret}
            onScroll={syncScroll}
            placeholder={placeholder}
            autoFocus={autoFocus}
            aria-label={ariaLabel}
            className="saisie"
            data-surligne={tokens.length > 0 || undefined}
          />
        </div>
        {menuOpen && (
          <div className="menu-liste" id={listId} role="listbox">
            {matches.map((p, idx) => (
              <button
                key={p.projectId}
                type="button"
                role="option"
                aria-selected={idx === selectedIdx}
                onMouseDown={(e) => {
                  e.preventDefault();
                  applyProject(p.name);
                }}
                onMouseEnter={() => setSelectedIdx(idx)}
                className="menu-option"
              >
                <span className="point" style={teinte(p.color)} />
                <span className="truncate">{p.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);
